// ─── Active navigation item ───────────────────────────────────────
// One rule for the sidebar, the bottom nav and anything else that highlights
// "where am I": the active path is the longest nav path that is the current
// path itself or one of its parents on a segment boundary. So /bills/123 lights
// Bills, /settings/email lights Email (not Settings) when both exist, and
// /billsx lights nothing.

/** A nav entry: its own path plus any highlight-only aliases. */
interface NavPathOwner {
  path: string;
  matchPaths?: string[];
  children?: NavPathOwner[];
}

/**
 * True when `path` is `current` or a parent of it on a segment boundary. A root
 * '/' or a '#hash' path therefore matches only itself.
 */
export function pathContains(path: string, current: string): boolean {
  return current === path || current.startsWith(`${path}/`);
}

/** The longest of `paths` that contains `current`, or undefined when none does. */
export function matchActivePath(paths: Iterable<string>, current?: string): string | undefined {
  if (!current) return undefined;
  let best: string | undefined;
  for (const path of paths) {
    if (path && pathContains(path, current) && (best === undefined || path.length > best.length)) {
      best = path;
    }
  }
  return best;
}

/** The paths an item answers to: its own and its `matchPaths`. */
export function navItemPaths(item: NavPathOwner): string[] {
  return item.matchPaths ? [item.path, ...item.matchPaths] : [item.path];
}

/** True when the resolved active path belongs to this item (not its children). */
export function itemOwnsPath(item: NavPathOwner, activePath?: string): boolean {
  return activePath !== undefined && navItemPaths(item).includes(activePath);
}

/** Resolve the active path across a nav tree, children and aliases included. */
export function activeNavPath(items: NavPathOwner[], current?: string): string | undefined {
  const paths: string[] = [];
  const collect = (list: NavPathOwner[]) => {
    for (const item of list) {
      paths.push(...navItemPaths(item));
      if (item.children) collect(item.children);
    }
  };
  collect(items);
  return matchActivePath(paths, current);
}
